import api from "./axiosConfig";

export interface ComplianceCheckItem {
  code: string;
  label: string;
  status: "Pass" | "Warning" | "Fail" | string;
  detail?: string | null;
}

export interface ComplianceStatus {
  clientId: string;
  onboardingCompliant: boolean;
  checklist: ComplianceCheckItem[];
}

export interface ClientComplianceOverview {
  clientId: string;
  firstName: string;
  lastName: string;
  email: string;
  overallStatus: "green" | "yellow" | "red" | string;
  onboardingCompliant: boolean;
  failCount: number;
  warningCount: number;
}

export interface ComplianceChecklistSummaryItem {
  code: string;
  label: string;
  status: string;
  detail: string;
}

export interface ComplianceAlert {
  severity: string;
  message: string;
  clientName?: string | null;
  clientId?: string | null;
}

export interface ComplianceDashboard {
  complianceScore: number;
  complianceStatus: string;
  totalClients: number;
  clientsWithContracts: number;
  clientsMissingContracts: number;
  clientsWithDisclosure: number;
  clientsMissingDisclosure: number;
  upfrontBillingViolations: number;
  aiGuardrailsActive: boolean;
  checklist: ComplianceChecklistSummaryItem[];
  alerts: ComplianceAlert[];
}

export interface ClientComplianceRow {
  clientId: string;
  clientName: string;
  hasContract: boolean;
  hasDisclosure: boolean;
  noUpfrontBilling: boolean;
  status: string;
  hasPendingContract: boolean;
}

export interface GeneratedComplianceDocument {
  title: string;
  content: string;
  format: string;
  generatedAt: string;
}

export interface CancellationStatus {
  clientId: string;
  agreementSignedAt?: string | null;
  windowEndsAt?: string | null;
  threeDayWindowHasPassed: boolean;
  wholeDaysRemaining?: number | null;
  message: string;
}

export const complianceApi = {
  getDashboard: async (): Promise<ComplianceDashboard> => {
    const { data } = await api.get<ComplianceDashboard>("/compliance");
    return data;
  },

  getClientComplianceList: async (): Promise<ClientComplianceRow[]> => {
    const { data } = await api.get<ClientComplianceRow[]>("/compliance/clients");
    return data;
  },

  getOverview: async (): Promise<ClientComplianceOverview[]> => {
    const { data } = await api.get<ClientComplianceOverview[]>("/compliance/overview");
    return data;
  },

  getStatus: async (clientId: string): Promise<ComplianceStatus> => {
    const { data } = await api.get<ComplianceStatus>(`/compliance/${clientId}/status`);
    return data;
  },

  generateDisclosure: async (clientId: string): Promise<GeneratedComplianceDocument> => {
    const { data } = await api.post<GeneratedComplianceDocument>(
      `/compliance/${clientId}/generate-disclosure`
    );
    return data;
  },

  getCancellationStatus: async (clientId: string): Promise<CancellationStatus> => {
    const { data } = await api.get<CancellationStatus>(`/compliance/${clientId}/cancellation-status`);
    return data;
  },
};
